"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplet, Flame, Gauge, Layers, PhoneCall, Sparkles, Wrench, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const issuesData = [{
  title: "Severe Drain Clogs",
  category: "Drainage",
  urgency: "High",
  description: "Water backing up in sinks, showers, or bathtubs. Usually caused by grease buildup, hair accumulation, or foreign objects obstructing the main line.",
  symptoms: "Slow draining, gurgling sounds, unpleasant odors",
  path: "/drain-problems/",
  iconName: "clog"
}, {
  title: "Hidden Water Leaks",
  category: "Pipes & Walls",
  urgency: "Urgent",
  description: "Unexplained spikes in water bills, damp drywall, or pooling water under fixtures. Unchecked leaks cause structural rot and hazardous mold growth.",
  symptoms: "Damp floors, spinning meter, musty smell",
  path: "/water-leak-problems/",
  iconName: "leak"
}, {
  title: "No Hot Water & Heater Failure",
  category: "Water Heater",
  urgency: "Same Day",
  description: "Lukewarm showers, pilot light issues, mineral tank corrosion, or total unit shutdown across gas and electric water heaters.",
  symptoms: "Discolored water, clicking noises, cold water supply",
  path: "/water-heater-problems/",
  iconName: "heater"
}, {
  title: "Toilet Overflows & Running Tanks",
  category: "Fixtures",
  urgency: "High",
  description: "Continuous running tanks wasting hundreds of gallons daily, weak flush mechanisms, or hazardous toilet bowl overflows.",
  symptoms: "Constant running water, weak siphon, wobbling base",
  path: "/toilet-problems/",
  iconName: "toilet"
}, {
  title: "Low Water Pressure",
  category: "Supply Line",
  urgency: "Moderate",
  description: "Weak flow throughout the house or localized to specific faucets. Frequently linked to faulty pressure regulators, pipe scale, or municipal line issues.",
  symptoms: "Dribbling faucets, inadequate shower stream",
  path: "/water-pressure-problems/",
  iconName: "pressure"
}, {
  title: "Sewer Line Backups",
  category: "Main Sewer",
  urgency: "Emergency",
  description: "Multiple fixtures backing up simultaneously with raw wastewater. Root intrusion or collapsed sewer laterals require immediate camera inspection.",
  symptoms: "Sewage odor in yard, multiple blocked drains",
  path: "/sewer-line-repair/",
  iconName: "sewer"
}, {
  title: "Burst or Ruptured Pipes",
  category: "Pipes",
  urgency: "Immediate",
  description: "Sudden pipe fractures causing active flooding. Demands immediate main shutoff operation and emergency pipe section replacement.",
  symptoms: "Hissing behind walls, active indoor pooling",
  path: "/emergency-plumbing-problems/",
  iconName: "burst"
}];
function getIssueIcon(type: string) {
  switch (type) {
    case "clog":
      return <Layers className="size-5 text-primary" />;
    case "leak":
      return <Droplet className="size-5 text-primary" />;
    case "heater":
      return <Flame className="size-5 text-primary" />;
    case "toilet":
      return <Wrench className="size-5 text-primary" />;
    case "pressure":
      return <Gauge className="size-5 text-primary" />;
    case "sewer":
      return <AlertTriangle className="size-5 text-destructive" />;
    case "burst":
      return <Zap className="size-5 text-destructive" />;
    default:
      return <Wrench className="size-5 text-primary" />;
  }
}
export default function PlumbingIssues() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="commonplumbingissues" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs uppercase tracking-wide">
              <Sparkles className="size-3.5 mr-1" />
              Ellenwood Diagnostic Guide
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            Common Plumbing Issues in Ellenwood
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From sudden pipe bursts and sewer line backups to stubborn clogs and cold water heaters, identify your plumbing breakdown below and request immediate on-site repair.
          </p>
        </div>

        {/* Grid of Issues */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {issuesData.map((issue, index) => <motion.div key={issue.title} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.06
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200">
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center">
                      {getIssueIcon(issue.iconName)}
                    </div>
                    <Badge variant={issue.urgency === "Emergency" || issue.urgency === "Immediate" ? "destructive" : "outline"} className="text-xs font-semibold">
                      {issue.urgency}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-default font-semibold text-card-foreground">
                    {issue.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground leading-normal">
                    {issue.description}
                  </p>
                  
                  <div className="rounded-md bg-muted/60 p-3 border border-border">
                    <span className="text-xs font-semibold text-foreground block mb-1 uppercase tracking-wider">
                      Key Indicators:
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {issue.symptoms}
                    </span>
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={issue.path}>
                      <span>Explore Repair Details</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}

          {/* Emergency Card Action */}
          <motion.div data-index={issuesData.length} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.4,
          delay: issuesData.length * 0.06
        }} className="flex">
            <Card className="flex flex-col justify-between w-full bg-primary text-primary-foreground border-primary shadow-sm">
              <CardHeader>
                <div className="size-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center mb-2">
                  <PhoneCall className="size-5 text-primary-foreground" />
                </div>
                <CardTitle className="text-xl font-default font-semibold text-primary-foreground">
                  Unsure of the Problem?
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <p className="text-sm text-primary-foreground/85 leading-normal">
                  Our licensed Ellenwood emergency technicians are available around the clock to diagnose strange noises, hidden water leaks, and sudden pressure drops.
                </p>
                <div className="rounded-md bg-primary-foreground/10 p-3 border border-primary-foreground/20">
                  <span className="text-xs font-medium text-primary-foreground block">
                    Fast response dispatch across Clayton & Henry County
                  </span>
                </div>
              </CardContent>

              <CardFooter className="pt-2">
                <Button asChild variant="secondary" className="w-full justify-center font-semibold">
                  <Link to="/emergency-plumbing/">
                    <span>Call Emergency Plumber</span>
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}